import { useState, useMemo } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { Button } from '../../ui/button';
import { CheckCircle, Link2, Shuffle, Send } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';
import { getDndBackend } from './dndBackend';
import { stripHtml } from '../../../../utils/slides/stripHtml';

interface Item {
  id: string;
  text: string;
  match: string;
}

interface DraggableItemProps {
  item: Item;
  type: 'left' | 'right';
  onSelect: (item: Item, type: 'left' | 'right') => void;
  onDrop?: (payload: {
    draggedId: string;
    draggedType: 'left' | 'right';
    targetId: string;
    targetType: 'left' | 'right';
  }) => void;
  matched?: boolean;
  selected?: boolean;
  disabled?: boolean;
}

function DraggableItem({ item, type, onSelect, onDrop, matched, selected, disabled }: DraggableItemProps) {
  const [{ isDragging }, drag] = useDrag({
    type: 'ITEM',
    item: { id: item.id, type },
    canDrag: !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'ITEM',
    canDrop: () => !disabled,
    drop: (draggedItem: { id: string; type: 'left' | 'right' }) => {
      if (draggedItem.type !== type && onDrop) {
        onDrop({
          draggedId: draggedItem.id,
          draggedType: draggedItem.type,
          targetId: item.id,
          targetType: type,
        });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <motion.button
      type="button"
      ref={(node) => {
        drag(drop(node));
      }}
      onClick={() => onSelect(item, type)}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={`${type === 'left' ? 'Term' : 'Definition'}: ${item.text}${selected ? ', selected' : ''}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={!disabled ? { scale: 1.02 } : undefined}
      whileTap={!disabled ? { scale: 0.97 } : undefined}
      className={`w-full min-h-11 p-3 md:p-4 rounded-2xl border-2 text-left transition-all touch-none ${
        disabled
          ? 'opacity-60 cursor-not-allowed'
          : isDragging
          ? 'opacity-40 scale-90 shadow-xl border-primary/50'
          : selected
          ? 'border-primary bg-primary/10 ring-2 ring-primary/30 shadow-lg'
          : matched
          ? 'bg-green-500/10 border-green-500/40 shadow-md shadow-green-500/10'
          : isOver
          ? 'border-primary bg-primary/10 shadow-lg shadow-primary/10 scale-[1.02]'
          : 'bg-card border-border/50 hover:border-primary/50 hover:shadow-lg cursor-move'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm md:text-base">{item.text}</span>
        {matched && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300 }}>
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          </motion.div>
        )}
      </div>
    </motion.button>
  );
}

export default function MatchingPairs({
  slide,
  onSubmit,
  onAnswerChange,
  hasSubmitted,
  className = '',
}: ActivitySlideProps) {
  const [matches, setMatches] = useState<Record<string, string>>({});
  const [selectedItem, setSelectedItem] = useState<{ id: string; type: 'left' | 'right' } | null>(null);
  const { content } = slide;
  const { backend, options } = getDndBackend();

  const buildItems = (): { leftItems: Item[]; rightItems: Item[] } => {
    const leftItemsRaw = Array.isArray(content?.leftItems) ? content.leftItems : null;
    const rightItemsRaw = Array.isArray(content?.rightItems) ? content.rightItems : null;
    const pairsRaw = Array.isArray(content?.pairs) ? content.pairs : null;

    if (leftItemsRaw && rightItemsRaw) {
      return {
        leftItems: leftItemsRaw.map((item: any, index: number) => ({
          id: item.id ?? `left-${index}`,
          text: item.text ?? item.left ?? '',
          match: item.match ?? `right-${index}`,
        })),
        rightItems: rightItemsRaw.map((item: any, index: number) => ({
          id: item.id ?? `right-${index}`,
          text: item.text ?? item.right ?? '',
          match: item.match ?? `left-${index}`,
        })),
      };
    }

    if (pairsRaw) {
      return {
        leftItems: pairsRaw.map((pair: any, index: number) => ({
          id: `left-${index}`,
          text: pair.left ?? '',
          match: `right-${index}`,
        })),
        rightItems: pairsRaw.map((pair: any, index: number) => ({
          id: `right-${index}`,
          text: pair.right ?? '',
          match: `left-${index}`,
        })),
      };
    }

    return { leftItems: [], rightItems: [] };
  };

  const { leftItems, rightItems: rawRightItems } = buildItems();

  // Shuffle right-side items so definitions don't appear in the same
  // order as the left-side terms (which would divulge the answers).
  // useMemo with slide.id keeps the shuffle stable during interaction.
  const rightItems = useMemo(() => {
    const arr = [...rawRightItems];
    // Fisher-Yates shuffle
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide.id]);

  const leftIdSet = new Set(leftItems.map((item) => item.id));
  const rightIdSet = new Set(rightItems.map((item) => item.id));

  const calculateCorrectCount = (currentMatches: Record<string, string>) =>
    leftItems.reduce((count, item) => (currentMatches[item.id] === item.match ? count + 1 : count), 0);

  const handleDrop = (payload: {
    draggedId: string;
    draggedType: 'left' | 'right';
    targetId: string;
    targetType: 'left' | 'right';
  }) => {
    const { draggedId, draggedType, targetId, targetType } = payload;
    const nextMatches = { ...matches };

    if (draggedType === 'left' && targetType === 'right' && leftIdSet.has(draggedId) && rightIdSet.has(targetId)) {
      nextMatches[draggedId] = targetId;
    } else if (draggedType === 'right' && targetType === 'left' && rightIdSet.has(draggedId) && leftIdSet.has(targetId)) {
      nextMatches[targetId] = draggedId;
    } else {
      return;
    }

    // Ensure each right item is only matched once
    Object.keys(nextMatches).forEach((leftId) => {
      if (leftId !== (draggedType === 'left' ? draggedId : targetId) && nextMatches[leftId] === (draggedType === 'left' ? targetId : draggedId)) {
        delete nextMatches[leftId];
      }
    });

    setMatches(nextMatches);
    setSelectedItem(null);
    const correctCount = calculateCorrectCount(nextMatches);
    const total = leftItems.length;
    const scorePercent = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    onAnswerChange({ matches: nextMatches, correctCount, total, scorePercent });
  };

  const handleSelect = (item: Item, type: 'left' | 'right') => {
    if (!selectedItem || selectedItem.type === type) {
      setSelectedItem(selectedItem?.id === item.id && selectedItem.type === type
        ? null
        : { id: item.id, type });
      return;
    }

    handleDrop({
      draggedId: selectedItem.id,
      draggedType: selectedItem.type,
      targetId: item.id,
      targetType: type,
    });
  };

  const handleSubmit = () => {
    if (leftItems.length === 0) return;
    const correctCount = calculateCorrectCount(matches);
    const total = leftItems.length;
    const scorePercent = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    onSubmit({ matches, correctCount, total, scorePercent });
  };

  const matchedCount = Object.entries(matches).filter(
    ([leftId, rightId]) => leftItems.find(l => l.id === leftId)?.match === rightId
  ).length;

  return (
    <DndProvider backend={backend} options={options}>
      <div className={`w-full ${className}`}>
        <div className="bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5 rounded-3xl p-5 sm:p-6 border border-border/40">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-purple-500/10 text-purple-600 px-4 py-1.5 rounded-full mb-3">
              <Shuffle className="w-4 h-4" />
              <span className="text-sm font-bold">Matching Pairs</span>
            </div>
            {(content?.showTitle !== false && content?.title && content.title !== 'Match the Pairs') && (
              <h2 className="text-lg font-bold mb-1">{stripHtml(content.title)}</h2>
            )}
            <div className="text-xs text-muted-foreground">
              {stripHtml(content?.instruction || content?.instructions || 'Match each term with its definition.')}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Tap one item, then tap its match. Dragging also works.
            </div>
            {selectedItem && (
              <div className="mt-2 text-sm font-medium text-primary" aria-live="polite">
                Selected: {(selectedItem.type === 'left' ? leftItems : rightItems).find(item => item.id === selectedItem.id)?.text}. Tap a matching item.
              </div>
            )}
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mt-3">
              <div className="flex gap-1.5">
                {leftItems.map((item, i) => (
                  <div key={i} className={`w-2.5 h-2.5 rounded-full transition-all ${
                    matches[item.id] ? 'bg-green-500 scale-110' : 'bg-muted-foreground/20'
                  }`} />
                ))}
              </div>
              <span className="text-xs text-muted-foreground font-medium">
                {Object.keys(matches).length} of {leftItems.length} matched
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-5">
            {/* Left Column */}
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                Terms
              </h3>
              {leftItems.map((item, idx) => (
                <DraggableItem
                  key={item.id}
                  item={item}
                  type="left"
                  onSelect={handleSelect}
                  selected={selectedItem?.id === item.id && selectedItem.type === 'left'}
                  matched={Boolean(matches[item.id])}
                  disabled={hasSubmitted}
                />
              ))}
            </div>

            {/* Right Column */}
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                Definitions
              </h3>
              {rightItems.map(item => (
                <DraggableItem
                  key={item.id}
                  item={item}
                  type="right"
                  onSelect={handleSelect}
                  onDrop={handleDrop}
                  selected={selectedItem?.id === item.id && selectedItem.type === 'right'}
                  matched={Object.values(matches).includes(item.id)}
                  disabled={hasSubmitted}
                />
              ))}
            </div>
          </div>

          {/* Connection Lines Visualization */}
          {Object.keys(matches).length > 0 && (
            <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/40 mb-5">
              <h4 className="text-sm font-bold text-muted-foreground flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4" />
                Your Matches
              </h4>
              <div className="space-y-2">
                {Object.entries(matches).map(([leftId, rightId], idx) => {
                  const leftItem = leftItems.find(i => i.id === leftId);
                  const rightItem = rightItems.find(i => i.id === rightId);
                  return (
                    <motion.div
                      key={leftId}
                      initial={{ opacity: 0, x: -10, scale: 0.95 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-center gap-2 p-2.5 rounded-xl border bg-green-500/10 border-green-500/30"
                    >
                      <div className="flex-1 font-medium text-sm truncate">{leftItem?.text}</div>
                      <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-green-600 text-white">
                        <CheckCircle className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 font-medium text-sm truncate text-right">{rightItem?.text}</div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="text-center">
            {hasSubmitted ? (
              <motion.div
                role="status"
                aria-live="polite"
                initial={{ opacity: 0, scale: 0.85, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 220, damping: 18 }}
                className="inline-flex items-center gap-3 rounded-2xl border border-green-500/20 bg-green-500/10 px-6 py-3 text-left"
              >
                <motion.div
                  initial={{ scale: 0, rotate: -45 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 300, delay: 0.15 }}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-600 text-white"
                >
                  <CheckCircle className="h-6 w-6" />
                </motion.div>
                <div>
                  <p className="font-bold text-green-700 dark:text-green-400">Answers submitted!</p>
                  <p className="text-sm text-muted-foreground">
                    {matchedCount} of {leftItems.length} pairs correct
                    {leftItems.length > 0 && ` · ${Math.round((matchedCount / leftItems.length) * 100)}%`}
                  </p>
                </div>
              </motion.div>
            ) : (
              <Button
                onClick={handleSubmit}
                size="lg"
                disabled={leftItems.length === 0 || Object.keys(matches).length === 0}
                className="rounded-2xl px-8 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit Answers
              </Button>
            )}
          </div>
        </div>
      </div>
    </DndProvider>
  );
}
